import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "wouter";
import { 
  FileText, 
  Plus,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Building2,
} from "lucide-react";
interface PressReleaseSubmissionWithFormData {
  id: string;
  type: string;
  formData: string;
  generatedContent: string | null;
  status: string | null;
  companyId: string | null;
  createdAt: Date | string | null;
}

export default function UserSubmissions() {
  const { data: submissions = [], isLoading } = useQuery<PressReleaseSubmissionWithFormData[]>({
    queryKey: ["/api/press-release-submissions/my"],
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "published":
        return <Badge className="bg-green-500/10 text-green-600 border-green-500/20"><CheckCircle2 className="h-3 w-3 mr-1" />Published</Badge>;
      case "generated":
        return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20"><CheckCircle2 className="h-3 w-3 mr-1" />Generated</Badge>;
      case "pending":
        return <Badge variant="secondary"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case "in_review":
        return <Badge variant="secondary"><AlertCircle className="h-3 w-3 mr-1" />In Review</Badge>;
      case "rejected":
        return <Badge variant="destructive"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold" data-testid="text-submissions-title">My Submissions</h1>
          <p className="text-muted-foreground">
            Track the status of your content submissions
          </p>
        </div>
        <Link href="/dashboard/press-releases">
          <Button data-testid="button-new-submission">
            <Plus className="h-4 w-4 mr-2" />
            New Submission
          </Button>
        </Link>
      </div>

      {submissions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Submissions Yet</h3>
            <p className="text-muted-foreground mb-4">
              Start creating content to see your submissions here
            </p>
            <Link href="/dashboard/content">
              <Button data-testid="button-go-to-content-studio">
                Go to Content Studio
              </Button>
            </Link>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {submissions.map((submission) => {
            const formData = JSON.parse(submission.formData || "{}");
            return (
              <Card key={submission.id} data-testid={`card-submission-${submission.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <CardTitle className="text-base">{formData.productName || "Untitled"}</CardTitle>
                      <CardDescription className="flex items-center gap-2 mt-1">
                        <Building2 className="h-3 w-3" />
                        {formData.companyName || "Unknown Company"}
                      </CardDescription>
                    </div>
                    {getStatusBadge(submission.status || "pending")}
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {formData.headline || "No headline"}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>
                      Created {submission.createdAt ? new Date(submission.createdAt).toLocaleDateString() : "Unknown"}
                    </span>
                    {submission.generatedContent && (
                      <Button variant="ghost" size="sm" className="h-7 text-xs">
                        View Content
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
